<template>
  <div class="p-6 dark:text-gray-50">
    <div class="flex space-x-3 mb-6">
      <h1 class="text-2xl font-semibold dark:text-white">Add Capture Config</h1>
    </div>

    <form @submit.prevent="submitForm" class="space-y-4 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">

      <div class="flex flex-col">
        <label class="mb-1 font-medium dark:text-white">Name<span class="pl-1 text-red-500">*</span></label>
        <input v-model="form.name" @input="onNameInput" type="text" required
          class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2"
          :class="[fieldErrors.name ? 'ring-2 ring-red-500' : 'focus:ring-blue-500']"
        />
        <FieldError :message="fieldErrors.name" />
      </div>

      <div class="flex flex-col">
        <label class="mb-1 font-medium dark:text-white">Probe<span class="pl-1 text-red-500">*</span></label>
        <GlassSelect
          v-model="form.probe"
          :options="captureProbes"
          :searchable="true"
          valueKey="id"
          labelKey="name"
          placeholder="-- Select capture probe --"
          :error="!!fieldErrors.probe"
          @change="onProbeChange"
        />
        <FieldError :message="fieldErrors.probe" />
      </div>

      <div class="flex flex-col">
        <label class="mb-1 font-medium dark:text-white">Interface<span class="pl-1 text-red-500">*</span></label>
        <GlassSelect
          v-if="canShowInterfaceDropdown"
          v-model="form.interface"
          :options="probeInterfaceOptions"
          :searchable="true"
          valueKey="value"
          labelKey="label"
          placeholder="-- Select interface on probe --"
          :error="!!fieldErrors.interface"
        />
        <input
          v-else
          v-model="form.interface"
          type="text"
          required
          :disabled="!form.probe"
          placeholder="e.g. enp3s0"
          class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2"
          :class="[fieldErrors.interface ? 'ring-2 ring-red-500' : 'focus:ring-blue-500']"
        />
        <div v-if="form.probe" class="text-xs mt-1 flex items-center space-x-3">
          <span v-if="probeInterfacesLoading" class="text-gray-500">Fetching interfaces from probe...</span>
          <span v-else-if="probeInterfacesError" class="text-gray-500">
            Probe unreachable ({{ probeInterfacesError }}). Falling back to manual entry.
          </span>
          <button
            v-if="probeInterfaces.length > 0"
            type="button"
            @click="useCustomInterface = !useCustomInterface"
            class="text-blue-600 underline"
          >
            {{ useCustomInterface ? 'Pick from probe list' : 'Enter a different interface' }}
          </button>
        </div>
        <div class="text-xs text-gray-500 mt-1">
          Promisc mode is enabled by the capture state after commit, so the interface doesn't need to be up yet.
        </div>
        <FieldError :message="fieldErrors.interface" />
      </div>

      <div class="flex flex-col">
        <label class="mb-1 font-medium dark:text-white">Capture filter</label>
        <BpfFilterBuilder v-model="form.bpf_filter" />
        <FieldError :message="fieldErrors.bpf_filter" />
      </div>

      <div class="flex flex-col">
        <label class="mb-1 font-medium dark:text-white">File Prefix<span class="pl-1 text-red-500">*</span></label>
        <input v-model="form.file_prefix" @input="onPrefixInput" type="text" required pattern="[A-Za-z0-9_-]+"
          placeholder="alphanumeric, _ and - only"
          class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2"
          :class="[fieldErrors.file_prefix ? 'ring-2 ring-red-500' : 'focus:ring-blue-500']"
        />
        <div class="text-xs text-gray-500 mt-1">Auto-populated from the name. Edit if you want a different filename prefix.</div>
        <FieldError :message="fieldErrors.file_prefix" />
      </div>

      <fieldset class="border border-gray-300 dark:border-gray-700 rounded-md p-4 space-y-3">
        <legend class="px-2 font-medium">Rotation</legend>
        <p class="text-xs text-gray-500 -mt-1">
          Pick at least one rotation trigger. Whichever fires first opens a new file.
        </p>

        <div class="flex flex-wrap items-center gap-2">
          <input type="checkbox" id="rot-filesize" v-model="rotate.filesize" @change="onRotateToggle('filesize')" class="h-4 w-4" />
          <label for="rot-filesize" class="font-medium w-52 flex-shrink-0">Rotate when file reaches</label>
          <div class="flex-1 min-w-[320px]">
            <FilesizeInput v-model="form.filesize_kb" :disabled="!rotate.filesize" />
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <input type="checkbox" id="rot-duration" v-model="rotate.duration" @change="onRotateToggle('duration')" class="h-4 w-4" />
          <label for="rot-duration" class="font-medium w-52 flex-shrink-0">Rotate after</label>
          <DurationInput v-model="form.rotate_duration_seconds" :disabled="!rotate.duration" />
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <input type="checkbox" id="rot-packets" v-model="rotate.packets" @change="onRotateToggle('packets')" class="h-4 w-4" />
          <label for="rot-packets" class="font-medium w-52 flex-shrink-0">Rotate after</label>
          <input type="number" v-model.number="form.rotate_packet_count" :disabled="!rotate.packets" min="1"
            class="w-32 border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 disabled:opacity-50" />
          <span>packets</span>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <input type="checkbox" id="rot-interval" v-model="rotate.interval" @change="onRotateToggle('interval')" class="h-4 w-4" />
          <label for="rot-interval" class="font-medium w-52 flex-shrink-0">Rotate on wall clock every</label>
          <DurationInput v-model="form.rotate_interval_seconds" :disabled="!rotate.interval" />
          <span class="text-xs text-gray-500">(e.g. every hour on the hour)</span>
        </div>

        <div class="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <label for="file-count" class="font-medium w-52 flex-shrink-0">Ring buffer keeps newest</label>
          <input id="file-count" type="number" v-model.number="form.file_count" min="1"
            class="w-32 border border-gray-300 dark:bg-gray-600 rounded px-2 py-1" />
          <span>files</span>
        </div>
        <FieldError :message="fieldErrors.filesize_kb" />
        <FieldError :message="fieldErrors.file_count" />
        <FieldError :message="fieldErrors.non_field_errors" />
      </fieldset>

      <fieldset class="border border-gray-300 dark:border-gray-700 rounded-md p-4 space-y-3">
        <legend class="px-2 font-medium">Stop condition (optional)</legend>
        <p class="text-xs text-gray-500 -mt-1">
          If set, dumpcap stops entirely once either of these is reached. Leave both unchecked to run until disabled.
        </p>

        <div class="flex flex-wrap items-center gap-2">
          <input type="checkbox" id="stop-packets" v-model="stop.packets" @change="onStopToggle('packets')" class="h-4 w-4" />
          <label for="stop-packets" class="font-medium w-52 flex-shrink-0">Stop capture after</label>
          <input type="number" v-model.number="form.stop_after_packets" :disabled="!stop.packets" min="1"
            class="w-32 border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 disabled:opacity-50" />
          <span>packets</span>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <input type="checkbox" id="stop-duration" v-model="stop.duration" @change="onStopToggle('duration')" class="h-4 w-4" />
          <label for="stop-duration" class="font-medium w-52 flex-shrink-0">Stop capture after</label>
          <DurationInput v-model="form.stop_after_duration_seconds" :disabled="!stop.duration" />
        </div>
      </fieldset>

      <div
        v-if="form.probe"
        class="rounded-md border p-3 text-sm"
        :class="budgetStyle.border"
      >
        <div v-if="telemetryLoading" class="flex items-center space-x-2 text-gray-500">
          <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z" />
          </svg>
          <span>Checking disk budget on probe...</span>
        </div>
        <div v-else-if="telemetryError" class="text-gray-600">
          Could not reach the probe to check disk budget ({{ telemetryError }}). Save will still be allowed; backend will recheck.
        </div>
        <div v-else-if="telemetry && !rotate.filesize">
          <div class="font-medium mb-2">Estimated disk usage</div>
          <div class="text-xs text-gray-500 mb-3">
            Filesize rotation is off, so the exact ring buffer size depends on traffic volume.
            Tweak the estimated bandwidth and packet size below to project how much disk this
            capture would consume.
          </div>
          <div class="grid grid-cols-2 gap-3 mb-3">
            <div v-if="rotate.duration || rotate.interval" class="flex items-center space-x-2">
              <label class="text-xs whitespace-nowrap">Expected bandwidth</label>
              <input type="number" v-model.number="estimate.mbps" min="0.1" step="0.1"
                class="w-20 border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 text-sm" />
              <span class="text-xs">Mbps</span>
            </div>
            <div v-if="rotate.packets" class="flex items-center space-x-2">
              <label class="text-xs whitespace-nowrap">Avg packet size</label>
              <input type="number" v-model.number="estimate.packetBytes" min="60" step="1"
                class="w-20 border border-gray-300 dark:bg-gray-600 rounded px-2 py-1 text-sm" />
              <span class="text-xs">bytes</span>
            </div>
          </div>
          <div class="space-y-1 text-sm">
            <div class="flex justify-between">
              <span>Estimated per-file size:</span>
              <span class="font-mono">~{{ formatBytes(estimatedPerFileBytes) }}</span>
            </div>
            <div class="flex justify-between">
              <span>Estimated ring buffer total:</span>
              <span class="font-mono">~{{ formatBytes(estimatedRingBufferBytes) }}
                (x {{ form.file_count }} files)</span>
            </div>
            <div class="flex justify-between">
              <span>Headroom available for new captures:</span>
              <span class="font-mono">{{ formatBytes(budgetBytes) }}</span>
            </div>
            <div class="flex justify-between font-medium pt-1 border-t border-current border-opacity-20"
              :class="estimateStyle.text">
              <span>Projected fill:</span>
              <span class="font-mono">{{ estimatedPct }}% of headroom</span>
            </div>
          </div>
          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded h-2 overflow-hidden mt-2">
            <div class="h-2" :class="estimateStyle.bar"
              :style="{ width: Math.min(estimatedPctNum, 100) + '%' }"></div>
          </div>
          <div v-if="estimatedPctNum >= 100" class="mt-2 text-red-700 text-sm">
            Projected ring buffer exceeds available headroom. Reduce duration, packet count, or file count.
          </div>
          <div class="text-xs text-gray-400 mt-2">
            Probe disk overall:
            <span class="font-mono">{{ formatBytes(diskUsedBytes) }}</span> of
            <span class="font-mono">{{ formatBytes(diskTotalBytes) }}</span> used.
            Estimates assume sustained traffic; actual usage can be lower during quiet periods.
          </div>
        </div>
        <div v-else-if="telemetry">
          <div class="font-medium mb-2">Disk budget for this probe</div>
          <div class="space-y-1">
            <div class="flex justify-between">
              <span>Already reserved by other captures:</span>
              <span class="font-mono">{{ formatBytes(existingReservation) }}</span>
            </div>
            <div class="flex justify-between" :class="budgetStyle.text">
              <span>This capture would add:</span>
              <span class="font-mono">{{ formatBytes(proposedReservation) }}</span>
            </div>
            <div class="flex justify-between font-medium pt-1 border-t border-current border-opacity-20">
              <span>Probe ring buffer limit:</span>
              <span class="font-mono">
                {{ formatBytes(totalReservation) }} / {{ formatBytes(budgetBytes) }}
                ({{ budgetPct }}%)
              </span>
            </div>
          </div>
          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded h-2 overflow-hidden mt-2">
            <div class="h-2" :class="budgetStyle.bar" :style="{ width: Math.min(budgetPctNum, 100) + '%' }"></div>
          </div>
          <div v-if="overBudget" class="mt-2 text-red-700 text-sm">
            Over limit. Reduce the filesize or ring buffer count, or disable another capture first.
          </div>
        </div>
      </div>

      <div class="flex items-center space-x-2">
        <input id="enabled" v-model="form.enabled" type="checkbox" class="h-4 w-4" />
        <label for="enabled" class="font-medium dark:text-white">Enabled (start capture immediately on commit)</label>
      </div>

      <div class="flex justify-end space-x-3 pt-4">
        <button type="button" @click="$router.back()"
          class="px-6 py-2 rounded-md bg-gray-300 text-gray-800 hover:bg-gray-400 dark:bg-gray-700 dark:text-white">Cancel</button>
        <button type="submit" :disabled="submitting || overBudget"
          class="bg-blue-700 text-white px-6 py-2 rounded-md hover:bg-blue-800 disabled:opacity-50">
          {{ submitting ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import {
  createCaptureConfig,
  getProbeTelemetry,
  getCaptureProbeInterfaces,
} from "@/services/captureService";
import { getAllProbes } from "@/services/probeService";
import FieldError from "@/components/FieldError.vue";
import BpfFilterBuilder from "@/components/BpfFilterBuilder.vue";
import FilesizeInput from "@/components/FilesizeInput.vue";
import DurationInput from "@/components/DurationInput.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { formatBytes } from "@/utils/formatBytes";

export default {
  name: "AddCapture",
  components: { FieldError, BpfFilterBuilder, FilesizeInput, DurationInput, GlassSelect },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      form: {
        name: "",
        probe: null,
        interface: "",
        bpf_filter: "",
        file_prefix: "",
        filesize_kb: 200000,
        file_count: 1000,
        rotate_duration_seconds: null,
        rotate_interval_seconds: null,
        rotate_packet_count: null,
        stop_after_packets: null,
        stop_after_duration_seconds: null,
        enabled: true,
      },
      rotate: {
        filesize: true,
        duration: false,
        interval: false,
        packets: false,
      },
      stop: {
        packets: false,
        duration: false,
      },
      estimate: {
        mbps: 10,
        packetBytes: 1500,
      },
      probes: [],
      probeInterfaces: [],
      probeInterfacesLoading: false,
      probeInterfacesError: "",
      useCustomInterface: false,
      prefixUserEdited: false,
      fieldErrors: {},
      submitting: false,
      telemetry: null,
      telemetryLoading: false,
      telemetryError: "",
    };
  },
  computed: {
    user() { return useStore.getUser(); },
    captureProbes() {
      return (this.probes || []).filter((p) => p.role === "capture");
    },
    selectedProbeSpanInterface() {
      if (!this.form.probe) return "";
      const probe = this.probes.find((p) => p.id === this.form.probe);
      return probe?.span_interface || "";
    },
    canShowInterfaceDropdown() {
      return (
        !!this.form.probe
        && this.probeInterfaces.length > 0
        && !this.useCustomInterface
      );
    },
    probeInterfaceOptions() {
      const span = this.selectedProbeSpanInterface;
      return this.probeInterfaces.map((iface) => ({
        value: iface,
        label: iface === span ? `${iface} (SPAN)` : iface,
      }));
    },
    existingReservation() {
      if (!this.telemetry) return 0;
      return this.telemetry.budget.reservation_bytes || 0;
    },
    proposedReservation() {
      if (!this.rotate.filesize) return 0;
      const kb = Number(this.form.filesize_kb) || 0;
      const count = Number(this.form.file_count) || 0;
      return kb * count * 1024;
    },
    totalReservation() {
      return this.existingReservation + this.proposedReservation;
    },
    budgetBytes() {
      return this.telemetry ? (this.telemetry.budget.budget_bytes || 0) : 0;
    },
    diskUsedBytes() {
      return this.telemetry ? (this.telemetry.disk.used_bytes || 0) : 0;
    },
    diskTotalBytes() {
      return this.telemetry ? (this.telemetry.disk.total_bytes || 0) : 0;
    },
    overBudget() {
      if (!this.telemetry) return false;
      if (!this.rotate.filesize) return false;
      return this.totalReservation > this.budgetBytes;
    },
    estimatedPerFileBytes() {
      // Estimate per-file ring buffer size when filesize-rotation is off.
      // Pick the smallest trigger since rotation is first-to-fire; whichever
      // fires first caps the file size.
      const candidates = [];
      const bps = (this.estimate.mbps || 0) * 1000000;
      if (this.rotate.duration && this.form.rotate_duration_seconds) {
        candidates.push(bps * this.form.rotate_duration_seconds / 8);
      }
      if (this.rotate.interval && this.form.rotate_interval_seconds) {
        candidates.push(bps * this.form.rotate_interval_seconds / 8);
      }
      if (this.rotate.packets && this.form.rotate_packet_count) {
        candidates.push(this.form.rotate_packet_count * (this.estimate.packetBytes || 1500));
      }
      if (candidates.length === 0) return 0;
      return Math.min(...candidates);
    },
    estimatedRingBufferBytes() {
      return this.estimatedPerFileBytes * (this.form.file_count || 0);
    },
    estimatedPctNum() {
      if (!this.budgetBytes) return 0;
      return (this.estimatedRingBufferBytes / this.budgetBytes) * 100;
    },
    estimatedPct() {
      const n = this.estimatedPctNum;
      if (n >= 10) return n.toFixed(0);
      return n.toFixed(1);
    },
    estimateStyle() {
      if (this.estimatedPctNum >= 100) return { bar: "bg-red-600", text: "text-red-700" };
      if (this.estimatedPctNum > 75) return { bar: "bg-yellow-500", text: "" };
      return { bar: "bg-blue-500", text: "" };
    },
    budgetPctNum() {
      if (!this.budgetBytes) return 0;
      return (this.totalReservation / this.budgetBytes) * 100;
    },
    budgetPct() {
      const n = this.budgetPctNum;
      if (n >= 10) return n.toFixed(0);
      return n.toFixed(1);
    },
    budgetStyle() {
      if (this.overBudget) {
        return {
          border: "border-red-400 bg-red-50 dark:bg-red-950 dark:border-red-700 text-red-900 dark:text-red-200",
          bar: "bg-red-600",
          text: "text-red-700",
        };
      }
      if (this.budgetPctNum > 75) {
        return {
          border: "border-yellow-400 bg-yellow-50 dark:bg-yellow-950 dark:border-yellow-700",
          bar: "bg-yellow-500",
          text: "",
        };
      }
      return {
        border: "border-gray-300 bg-gray-50 dark:bg-gray-800 dark:border-gray-700",
        bar: "bg-blue-500",
        text: "",
      };
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    if (this.tenant.currentOrganizationId.value) {
      // Org already pinned (back-navigation or ?client= refresh):
      // loadInitialData() skips the fetch when the org is unchanged.
      await this.tenant.syncUrlParam();
      await this.fetchProbes(this.tenant.currentSchemaName.value);
    } else {
      await this.tenant.loadInitialData(this.fetchProbes);
    }
    const probeParam = this.$route.query?.probe;
    if (probeParam) {
      const probeId = parseInt(probeParam, 10);
      if (!Number.isNaN(probeId) && this.captureProbes.some((p) => p.id === probeId)) {
        this.form.probe = probeId;
        this.onProbeChange();
      }
    }
  },
  methods: {
    formatBytes,
    async fetchProbes(schemaName = null) {
      try {
        this.probes = await getAllProbes(schemaName);
      } catch (e) {
        console.error("Error fetching probes:", e);
      }
    },
    onNameInput(e) {
      if (this.prefixUserEdited) return;
      const name = e.target.value || "";
      this.form.file_prefix = name
        .replace(/[^A-Za-z0-9_-]+/g, "_")
        .replace(/^_+|_+$/g, "");
    },
    onPrefixInput() {
      this.prefixUserEdited = true;
    },
    onProbeChange() {
      this.form.interface = "";
      this.probeInterfaces = [];
      this.probeInterfacesError = "";
      this.useCustomInterface = false;
      this.fetchProbeInterfaces();
      this.fetchTelemetry();
    },
    async fetchProbeInterfaces() {
      if (!this.form.probe) return;
      this.probeInterfacesLoading = true;
      try {
        const list = await getCaptureProbeInterfaces(
          this.form.probe,
          this.tenant.currentSchemaName?.value,
        );
        this.probeInterfaces = Array.isArray(list) ? list : [];
        // Auto-pick SPAN interface if known and present in the list.
        const span = this.selectedProbeSpanInterface;
        if (span && this.probeInterfaces.includes(span)) {
          this.form.interface = span;
        }
      } catch (e) {
        this.probeInterfaces = [];
        this.probeInterfacesError = e?.response?.data?.error || e.message || "unreachable";
      } finally {
        this.probeInterfacesLoading = false;
      }
    },
    async fetchTelemetry() {
      if (!this.form.probe) {
        this.telemetry = null;
        return;
      }
      this.telemetryLoading = true;
      this.telemetryError = "";
      try {
        this.telemetry = await getProbeTelemetry(this.form.probe, {
          refresh: true,
          schemaName: this.tenant.currentSchemaName?.value,
        });
      } catch (e) {
        this.telemetry = null;
        this.telemetryError = e?.response?.data?.error || e.message || "unreachable";
      } finally {
        this.telemetryLoading = false;
      }
    },
    onRotateToggle(key) {
      // Uncheck clears the corresponding form field so the backend sees null.
      if (!this.rotate[key]) {
        if (key === "filesize") this.form.filesize_kb = null;
        if (key === "duration") this.form.rotate_duration_seconds = null;
        if (key === "interval") this.form.rotate_interval_seconds = null;
        if (key === "packets") this.form.rotate_packet_count = null;
      } else {
        if (key === "filesize" && !this.form.filesize_kb) this.form.filesize_kb = 200000;
        if (key === "duration" && !this.form.rotate_duration_seconds) this.form.rotate_duration_seconds = 600;
        if (key === "interval" && !this.form.rotate_interval_seconds) this.form.rotate_interval_seconds = 3600;
        if (key === "packets" && !this.form.rotate_packet_count) this.form.rotate_packet_count = 100000;
      }
    },
    onStopToggle(key) {
      if (!this.stop[key]) {
        if (key === "packets") this.form.stop_after_packets = null;
        if (key === "duration") this.form.stop_after_duration_seconds = null;
      } else {
        if (key === "packets" && !this.form.stop_after_packets) this.form.stop_after_packets = 100000;
        if (key === "duration" && !this.form.stop_after_duration_seconds) this.form.stop_after_duration_seconds = 300;
      }
    },
    async submitForm() {
      this.fieldErrors = {};
      if (!this.rotate.filesize && !this.rotate.duration && !this.rotate.interval && !this.rotate.packets) {
        this.fieldErrors.non_field_errors = "Pick at least one rotation trigger so the capture has a bound.";
        return;
      }
      this.submitting = true;
      try {
        const payload = {
          name: this.form.name,
          probe: this.form.probe,
          interface: this.form.interface,
          bpf_filter: this.form.bpf_filter,
          file_prefix: this.form.file_prefix,
          file_count: this.form.file_count,
          enabled: this.form.enabled,
          filesize_kb: this.rotate.filesize ? this.form.filesize_kb : null,
          rotate_duration_seconds: this.rotate.duration ? this.form.rotate_duration_seconds : null,
          rotate_interval_seconds: this.rotate.interval ? this.form.rotate_interval_seconds : null,
          rotate_packet_count: this.rotate.packets ? this.form.rotate_packet_count : null,
          stop_after_packets: this.stop.packets ? this.form.stop_after_packets : null,
          stop_after_duration_seconds: this.stop.duration ? this.form.stop_after_duration_seconds : null,
        };
        await createCaptureConfig(payload, this.tenant.currentSchemaName?.value);
        this.$router.push({ name: "CaptureView", query: this.tenant.addSchemaParam() });
      } catch (err) {
        const data = err?.response?.data;
        if (data && typeof data === "object") {
          for (const [k, v] of Object.entries(data)) {
            this.fieldErrors[k] = Array.isArray(v) ? v.join(" ") : String(v);
          }
        } else {
          alert("Failed to create capture config.");
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
